// src/lib/amenities.js
// Single-source-of-truth for amenity keys, labels, and normalization.

export const AMENITY_CATALOG = {
  // Connectivity
  wifi: { label: "Wi-Fi" },
  "wi fi": { label: "Wi-Fi" },
  "wi-fi": { label: "Wi-Fi" },
  internet: { label: "Wi-Fi" },

  // Core home
  kitchen: { label: "Full kitchen" },
  tv: { label: "TV" },
  washer: { label: "Washer" },
  dryer: { label: "Dryer" },

  // Comfort
  ac: { label: "Air conditioning" },
  "air conditioning": { label: "Air conditioning" },
  heating: { label: "Heating" },
  workspace: { label: "Dedicated workspace" },

  // Kitchen extras (match CreateListing groups)
  refrigerator: { label: "Refrigerator" },
  microwave: { label: "Microwave" },
  dishwasher: { label: "Dishwasher" },
  "coffee maker": { label: "Coffee maker" },
  "cooking basics": { label: "Cooking basics" },
  oven: { label: "Oven" },
  stove: { label: "Stove" },

  // Facilities
  pool: { label: "Pool" },
  "hot tub": { label: "Hot tub" },
  hottub: { label: "Hot tub" },
  gym: { label: "Gym" },

  // Parking / EV / outdoor
  "free parking": { label: "Free parking" },
  parking: { label: "Free parking" },
  "ev charger": { label: "EV charger" },
  ev: { label: "EV charger" },
  patio: { label: "Patio / deck" },
  "indoor fireplace": { label: "Indoor fireplace" },

  // Family
  crib: { label: "Crib" },
  "high chair": { label: "High chair" },
  bath: { label: "Bathtub" },
  "board games": { label: "Board games" },

  // Personal care / laundry
  "hair dryer": { label: "Hair dryer" },
  hairdryer: { label: "Hair dryer" },
  iron: { label: "Iron" },

  // Safety
  "smoke alarm": { label: "Smoke alarm" },
  "carbon monoxide alarm": { label: "CO alarm" },
  "first aid kit": { label: "First aid kit" },
  "fire extinguisher": { label: "Fire extinguisher" },
  "security cameras": { label: "Security cameras (disclosed)" },

  // Accessibility (self-reported)
  "step free access": { label: "Step-free access" },
  "wide doorway": { label: "Wide doorway" },
  "accessible parking": { label: "Accessible parking" },

  // Misc / legacy Airbnb-ish stuff
  essentials: { label: "Essentials" },
  "private entrance": { label: "Private entrance" },
  backyard: { label: "Backyard" },
  garden: { label: "Backyard" },

  bbq: { label: "BBQ grill" },
  "bbq grill": { label: "BBQ grill" },
  breakfast: { label: "Breakfast" },
  pets: { label: "Pets allowed" },
  "pets allowed": { label: "Pets allowed" },

  "long term stays allowed": { label: "Long-term stays" },
  "self check-in": { label: "Self check-in" },
  keypad: { label: "Keypad" },
};

// For UI ordering on detail pages (after normalization to canonical keys)
export const AMENITY_ORDER = [
  // Connectivity & essentials
  "wifi",
  "tv",
  "workspace",

  // Kitchen
  "kitchen",
  "refrigerator",
  "microwave",
  "dishwasher",
  "coffee maker",
  "cooking basics",
  "oven",
  "stove",

  // Laundry & comfort
  "washer",
  "dryer",
  "ac",
  "heating",
  "hair dryer",
  "iron",
  "essentials",

  // Family
  "crib",
  "high chair",
  "bath",
  "board games",

  // Facilities & outdoor
  "pool",
  "hot tub",
  "gym",
  "patio",
  "indoor fireplace",
  "backyard",
  "bbq grill",

  // Parking & EV & accessibility
  "free parking",
  "ev charger",
  "accessible parking",
  "step free access",
  "wide doorway",
  "private entrance",

  // Safety
  "smoke alarm",
  "carbon monoxide alarm",
  "first aid kit",
  "fire extinguisher",
  "security cameras",

  // Policies / misc
  "pets allowed",
  "breakfast",
  "long term stays allowed",
  "self check-in",
  "keypad",
];

// Normalize: fix unicode hyphens, handle camelCase, strip punctuation to spaces, collapse spaces.
// Then map common aliases to canonical keys we actually save/show.
export function normalizeAmenityKey(x) {
  const raw = String(x || "");

  const base = raw
    // split camelCase (smokeAlarm -> smoke Alarm)
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .normalize("NFKD")
    .toLowerCase()
    // unify hyphen-like chars to hyphen
    .replace(/[\u2010-\u2015]/g, "-")
    // turn *non* alphanum / hyphen / space into spaces (kills underscores -> spaces)
    .replace(/[^a-z0-9- ]+/g, " ")
    // treat hyphens as spaces for matching
    .replace(/[-]+/g, " ")
    // collapse spaces
    .replace(/\s+/g, " ")
    .trim();

  const ALIAS = {
    // wifi / internet
    "wi fi": "wifi",
    "wi-fi": "wifi",
    internet: "wifi",

    // comfort
    "air conditioning": "ac",
    hairdryer: "hair dryer",

    // spelling / join variants
    hottub: "hot tub",
    garden: "backyard",
    parking: "free parking",
    ev: "ev charger",

    // safety short forms
    "co alarm": "carbon monoxide alarm",
    "first aid": "first aid kit",
    smokealarm: "smoke alarm",
    carbonmonoxidealarm: "carbon monoxide alarm",
    firstaidkit: "first aid kit",

    // EV as one word
    evcharger: "ev charger",

    // check-in aliases
    "self check in": "self check-in",
    "long term stays": "long term stays allowed",
    "long-term stays": "long term stays allowed",

    // exact keypad passthrough (in case of weird input)
    keypad: "keypad",
  };

  const canon = ALIAS[base] || base;

  // If our catalog knows this key as-is, use it.
  if (AMENITY_CATALOG[canon]) return canon;

  // Otherwise just return canon; ListingDetail can show the raw label.
  return canon;
}

// Build a stable array of { key, label } for showing on detail page
export function toAmenityList(keysOrAnything) {
  // Accept: comma-/newline-separated string | array of strings | array of objects | boolean map object
  const push = (arr, key, label) => {
    const k = normalizeAmenityKey(key);
    if (!k) return;
    const lbl = AMENITY_CATALOG[k]?.label || label || key;
    if (!arr.find((x) => x.key === k)) arr.push({ key: k, label: lbl });
  };

  const out = [];

  // Handle "Wi-Fi, Kitchen, TV" or bullet/newline lists
  if (typeof keysOrAnything === "string") {
    for (const token of keysOrAnything.split(/[,\n]|•/g)) {
      const t = token.trim();
      if (t) push(out, t);
    }
    return out;
  }

  if (Array.isArray(keysOrAnything)) {
    for (const item of keysOrAnything) {
      if (!item) continue;
      if (typeof item === "string") {
        push(out, item);
      } else if (typeof item === "object") {
        const raw = item.key || item.id || item.slug || item.name || item.label;
        push(out, raw, item.label || item.name);
      }
    }
  } else if (keysOrAnything && typeof keysOrAnything === "object") {
    for (const [k, v] of Object.entries(keysOrAnything)) {
      if (v) push(out, k);
    }
  }

  return out;
}
